/**
 * 認証エラーコード→日本語メッセージ変換
 *
 * 役割: APIから返されるエラーコード(code)を
 *       ユーザー向けの日本語メッセージに変換する
 *
 * バックエンドは英語のerrorフィールド（開発者向け）と
 * codeフィールド（プログラム識別用）を返す。
 * 表示メッセージの決定はプレゼンテーション層の責務。
 */

import { isAxiosError } from 'axios';

/** 認証エラーコード */
type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_DISABLED'
  | 'EMAIL_SEND_FAILED'
  | 'EMAIL_NOT_CONFIGURED'
  | 'INVALID_VERIFICATION_CODE'
  | 'VERIFICATION_CODE_EXPIRED'
  | 'TOO_MANY_ATTEMPTS'
  | 'SESSION_EXPIRED'
  | 'RESEND_COOLDOWN'
  | 'USER_NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'RATE_LIMIT_EXCEEDED'
  | 'INTERNAL_ERROR';

/** エラーコード→日本語メッセージマッピング */
const AUTH_ERROR_MESSAGES: Readonly<Record<AuthErrorCode, string>> = {
  INVALID_CREDENTIALS: 'ユーザー名またはパスワードが正しくありません',
  ACCOUNT_DISABLED: 'このアカウントは無効化されています',
  EMAIL_SEND_FAILED: '認証コードの送信に失敗しました。しばらくしてからもう一度お試しください',
  EMAIL_NOT_CONFIGURED: '2FA用のメールアドレスが設定されていません。管理者にお問い合わせください',
  INVALID_VERIFICATION_CODE: '認証コードが正しくありません',
  VERIFICATION_CODE_EXPIRED: '認証コードの有効期限が切れました。再度ログインしてください',
  TOO_MANY_ATTEMPTS: '試行回数の上限に達しました。再度ログインして新しいコードを取得してください',
  SESSION_EXPIRED: 'セッションの有効期限が切れました。再度ログインしてください',
  RESEND_COOLDOWN: 'しばらくしてからもう一度お試しください',
  USER_NOT_FOUND: 'ユーザーが見つかりません',
  VALIDATION_ERROR: '入力内容に誤りがあります',
  RATE_LIMIT_EXCEEDED: 'リクエスト回数の上限に達しました。しばらくしてからお試しください',
  INTERNAL_ERROR: 'サーバーエラーが発生しました。しばらくしてからもう一度お試しください',
};

/** APIエラーレスポンスの型 */
interface ApiErrorResponse {
  readonly code?: string;
  readonly error?: string;
}

/**
 * エラーコードが既知の認証エラーコードかどうかを判定
 */
const isKnownAuthErrorCode = (code: string): code is AuthErrorCode => {
  return code in AUTH_ERROR_MESSAGES;
};

/**
 * エラーオブジェクトからユーザー向け日本語メッセージを取得
 *
 * 優先順位:
 * 1. APIレスポンスのcodeフィールドから日本語マッピング
 * 2. フォールバックメッセージ
 */
export const getAuthErrorMessage = (params: {
  readonly error: unknown;
  readonly fallbackMessage: string;
}): string => {
  const { error, fallbackMessage } = params;

  if (isAxiosError(error)) {
    const responseData = error.response?.data as ApiErrorResponse | undefined;
    const errorCode = responseData?.code;

    if (errorCode !== undefined && isKnownAuthErrorCode(errorCode)) {
      return AUTH_ERROR_MESSAGES[errorCode];
    }
  }

  return fallbackMessage;
};
